import type { components as definitions } from "@securityjourney/svc-chores/dist/gen/chores.d.ts";
import type { ChoreList } from "../client/svcChores";

type ChoreItem = ChoreList["items"][number];
type Lesson = definitions["schemas"]["securityjourney.proto.chores.Lesson"];

export type LessonChoreItem = ChoreItem & {
  content: { lesson: Lesson };
};

export interface PersonalizedLessonAssignment {
  name: string;
  description: string;
  /** Resolved due date (ISO string); undefined when the schedule has no due window */
  dueDate: string | undefined;
  totalLessons: number;
  completedLessons: number;
  progressPercent: number;
  completed: LessonChoreItem[];
  notCompleted: LessonChoreItem[];
}

function isLessonItem(item: ChoreItem): item is LessonChoreItem {
  return "lesson" in item.content;
}

// Resolve the schedule to a concrete due date: availableAt + dueAfter.days - 1
// (same convention as the assessments due-date calculation in learn).
function resolveDueDate(dates: ChoreItem["schedule"]["dates"]): string | undefined {
  if (!dates || dates.dueAfter?.days == null) return undefined;

  const due = new Date(dates.availableAt);
  due.setDate(due.getDate() + dates.dueAfter.days - 1);

  return due.toISOString();
}

export function getPersonalizedLessonAssignment(
  choreItems: ChoreList["items"] | undefined
): PersonalizedLessonAssignment | null {
  const lessons = choreItems?.filter(isLessonItem) ?? [];
  if (!lessons.length) return null;

  const completed: LessonChoreItem[] = [];
  const notCompleted: LessonChoreItem[] = [];
  lessons.forEach(item => {
    if (item.content.lesson.status.progress === "Completed") {
      completed.push(item);
    } else {
      notCompleted.push(item);
    }
  });

  const totalLessons = lessons.length;
  const completedLessons = completed.length;
  const progressPercent = totalLessons ? Math.round((completedLessons / totalLessons) * 100) : 0;

  const firstLesson = lessons[0];
  if (!firstLesson) return null;

  return {
    name: firstLesson.listing.title,
    description: firstLesson.listing.description,
    dueDate: resolveDueDate(firstLesson.schedule.dates),
    totalLessons,
    completedLessons,
    progressPercent,
    completed,
    notCompleted,
  };
}
